/**
 * Derived, serializable socket state — and nothing else.
 *
 * The live connection belongs to `@services/socket.service`; this module holds
 * only what components need to render, mirrored across by
 * `store/plugins/socket.js`. Keeping the Echo instance out of Vuex is the point
 * of the refactor: Vue 2 deeply observes store state, so the pusher-js object
 * graph used to get reactive getters installed across it.
 */
const state = {
    channelUsers: [],
    // Mirrored from socket.service by `store/plugins/socket.js`. The connection
    // itself deliberately does not live here — see that service's header.
    socketStatus: '',
    subscribedChannels: []
};

const mutations = {
    ['ADD_CHANNEL_USER'](state, user) {
        if(!state.channelUsers.find(u => u.id === user.id)) {
            state.channelUsers.push(user);
        }
    },
    ['REMOVE_CHANNEL_USER'](state, user) {
        state.channelUsers = state.channelUsers.filter(u => u.id !== user.id);
    },
    ['CLEAR_CHANNEL_USERS'](state) {
        state.channelUsers = [];
    },
    ['SET_SOCKET_STATUS'](state, status) {
        state.socketStatus = status;
    },
    ['SET_SUBSCRIBED_CHANNELS'](state, names) {
        state.subscribedChannels = names;
    },
    /**
     * Replaces the roster rather than merging into it.
     *
     * `here()` fires again every time pusher resubscribes, and any `leaving`
     * events that happened while the socket was down are gone for good — so
     * appending would leave people in the list who already left. The incoming
     * list is always authoritative.
     */
    ['SET_CHANNEL_USERS'](state, users) {
        state.channelUsers = users;
    }
};

const actions = {
    /**
     * The only action left: `routes/routes.js` clears the roster on navigation,
     * and a route guard has no component instance to commit through.
     */
    ['CLEAR_CHANNEL_USERS']({ commit }) {
        commit('CLEAR_CHANNEL_USERS');
    }
};

const getters = {
    channelUsers: state => state.channelUsers,
    socketStatus: state => state.socketStatus,
    subscribedChannels: state => state.subscribedChannels,
    isSocketConnected: state => state.socketStatus === 'connected'
};

export default {
    state,
    mutations,
    actions,
    getters
};
